import { isoDate as melDate } from "@/lib/intl";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, UserMinus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg } from "./kpiFormat";

const TASK_LABEL = { not_started: "Not started", in_progress: "In progress", waiting: "Waiting", submitted_for_review: "In review",
  completed: "Done", cancelled: "Cancelled", deleted: "Task deleted" };
const plusDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return melDate(d); };

const TaskDialog = ({ week, member, onClose, onSaved }) => {
  const [users, setUsers] = useState([]);
  const [assignee, setAssignee] = useState("");
  const [due, setDue] = useState(plusDays(2));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/kpi/assignees").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/kpi/reports/${week}/follow-ups`, {
        client_id: member.client_id, client_name: member.client_name, product: member.product,
        last_visit_label: member.last_visit ? fmtDate(member.last_visit) : "", assignee_user_id: assignee, due_at: due || null, note,
      });
      toast.success("Follow-up task created");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="kpi-followup-dialog">
        <DialogHeader>
          <DialogTitle>Follow up {member.client_name || member.client_email || member.client_id}</DialogTitle>
          <DialogDescription>{member.product} · last visit {member.last_visit ? fmtDate(member.last_visit) : "none recorded"}. The task appears in the team member's Tasks.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Who should follow up?</Label>
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger data-testid="kpi-followup-assignee"><SelectValue placeholder="Pick a team member" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`kpi-followup-assignee-${u.id}`}>
                {u.name} <span className="text-zinc-400">· {u.role}</span></SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="kpi-followup-due">Due</Label>
            <Input id="kpi-followup-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="kpi-followup-due" />
          </div>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000}
            placeholder="Note for the team member (optional)" data-testid="kpi-followup-note" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="kpi-followup-cancel">Cancel</Button>
          <Button onClick={save} disabled={!assignee || busy} data-testid="kpi-followup-save">Create task</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MemberRow = ({ m, task, onTask }) => (
  <tr className="border-t border-zinc-100 align-top" data-testid={`kpi-novisit-row-${m.client_id}`}>
    <td className="py-2 pr-3">
      {m.client_email ? <Link to={`/clients/${encodeURIComponent(m.client_email)}`} className="font-medium text-zinc-900 hover:text-blue-700 hover:underline">
        {m.client_name || m.client_email}</Link> : <span className="font-medium">{m.client_name || m.client_id}</span>}
      {m.client_name && m.client_email && <div className="text-2xs text-zinc-500">{m.client_email}</div>}
    </td>
    <td className="py-2 pr-3 text-xs text-zinc-600">{m.product}</td>
    <td className="py-2 pr-3 font-mono-num text-xs whitespace-nowrap">{m.last_visit ? fmtDate(m.last_visit) : "No visits recorded"}</td>
    <td className="py-2 pr-3 font-mono-num text-xs">{m.days_since ?? "—"}</td>
    <td className="py-2 text-right">
      {task ? (
        <span className="text-2xs text-zinc-600" data-testid={`kpi-novisit-task-${m.client_id}`}>
          <ClipboardList className="inline h-3.5 w-3.5 mr-1 text-zinc-400" />{task.assignee_name} · {TASK_LABEL[task.task_status] || task.task_status}
        </span>
      ) : (
        <Button size="sm" variant="outline" onClick={() => onTask(m)} data-testid={`kpi-novisit-create-${m.client_id}`}>
          <ClipboardList className="h-3.5 w-3.5 mr-1" />Create task
        </Button>
      )}
    </td>
  </tr>
);

export const NoVisitFollowUps = ({ week, onOpenList }) => {
  const [d, setD] = useState(null);
  const [tasks, setTasks] = useState({});
  const [member, setMember] = useState(null);
  const load = useCallback(() => {
    api.get(`/kpi/reports/${week}/drill/no_visit_members`, { timeout: 120000 }).then((r) => setD(r.data)).catch(() => setD({ rows: [] }));
    api.get(`/kpi/reports/${week}/follow-ups`).then((r) => setTasks(Object.fromEntries(r.data.rows.map((t) => [t.client_id, t]))))
      .catch(() => setTasks({}));
  }, [week]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="h-16 animate-pulse rounded-md bg-zinc-50" />;
  return (
    <div className="rounded-md border border-zinc-200 p-4 space-y-3" data-testid="kpi-novisit-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <UserMinus className="h-4 w-4 text-zinc-500" />
          <span className="font-medium text-zinc-900" data-testid="kpi-novisit-count">
            {d.rows.length} active member{d.rows.length === 1 ? "" : "s"} had no visits this week</span>
          <span className="text-xs text-zinc-500">· paused members left out · {Object.keys(tasks).length} follow-up task(s)</span>
        </div>
        {d.rows.length > 0 && <Button size="sm" variant="ghost" onClick={onOpenList} data-testid="kpi-novisit-open-list">Full list & CSV</Button>}
      </div>
      {d.source === "live" && d.rows.length > 0 && (
        <p className="text-2xs text-amber-700" data-testid="kpi-novisit-live-note">Worked out from today's data — refresh the draft to save it with the report.</p>
      )}
      {d.rows.length > 0 && (
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white"><tr className="text-left text-2xs uppercase tracking-[0.16em] text-zinc-500">
              <th className="pb-2">Member</th><th className="pb-2">Membership / pack</th><th className="pb-2">Last visit</th>
              <th className="pb-2">Days since</th><th /></tr></thead>
            <tbody>{d.rows.map((m) => <MemberRow key={m.client_id} m={m} task={tasks[m.client_id]} onTask={setMember} />)}</tbody>
          </table>
        </div>
      )}
      {member && <TaskDialog week={week} member={member} onClose={() => setMember(null)} onSaved={() => { setMember(null); load(); }} />}
    </div>
  );
};
